import categoriesAndSubcategoriesService from "../services/fetchCategoriesAndSubcategories";
import { getSession, setSession, deleteSession } from "./session";
import { isShopStockManager } from "./roles";
import * as Sentry from "@sentry/react";

/**
 * Role-scoped cache for the shop's allowed categories.
 *
 * The `/admin/config/getCategoriesAndSubcategories` endpoint returns the set of
 * categories the CURRENT user is allowed to work with, scoped by `x-shop-id`.
 * For a SHOP_STOCK_MANAGER this is their restricted set and it is used to trim
 * the taxonomy dropdowns on the product create/update form.
 *
 * Stock managers hit this on every product form open, so we cache the response
 * in session storage (localStorage-backed, like the rest of the auth session)
 * for a short TTL to avoid repeated network calls. The invite/employee flow is
 * intentionally NOT cached here - it keeps using the redux slice directly so an
 * admin always sees fresh data. Stock managers cannot reach the invite flow, so
 * scoping the cache by role keeps the two paths cleanly separated.
 */

// Response shape under result.data: top level category (L1_sub_domain) -> list
// of subcategory names (L2_category).
export interface AllowedCategoriesMap {
  [l1SubDomain: string]: string[];
}

interface CachedAllowedCategories {
  data: AllowedCategoriesMap;
  expiresAt: number;
}

// Cache entries are keyed by shop so a shop-context switch never reuses the
// wrong shop's categories. The prefix lets us clear every entry on logout.
const CACHE_KEY_PREFIX = "scm_allowed_categories_";
const TTL_MS = 15 * 60 * 1000; // 15 minutes

const cacheKeyFor = (shopId: string): string => `${CACHE_KEY_PREFIX}${shopId}`;

const readCache = (shopId: string): AllowedCategoriesMap | null => {
  const raw = getSession(cacheKeyFor(shopId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CachedAllowedCategories;
    if (!parsed?.expiresAt || Date.now() > parsed.expiresAt) {
      deleteSession(cacheKeyFor(shopId));
      return null;
    }
    return parsed.data ?? null;
  } catch {
    // Corrupt entry - drop it and refetch.
    deleteSession(cacheKeyFor(shopId));
    return null;
  }
};

const writeCache = (shopId: string, data: AllowedCategoriesMap): void => {
  const entry: CachedAllowedCategories = {
    data,
    expiresAt: Date.now() + TTL_MS,
  };
  setSession(cacheKeyFor(shopId), JSON.stringify(entry));
};

/**
 * Returns the logged-in stock manager's allowed categories for the given shop,
 * reading from a short-lived session cache and fetching on a miss.
 *
 * Returns `null` when the current user is NOT a stock manager (callers should
 * then apply no category restriction) or when no shopId is available.
 */
export const getStockManagerAllowedCategories = async (
  shopId: string | undefined | null
): Promise<AllowedCategoriesMap | null> => {
  const role = getSession("role");
  if (!isShopStockManager(role) || !shopId) return null;

  const cached = readCache(shopId);
  if (cached) return cached;

  try {
    const response =
      await categoriesAndSubcategoriesService.fetchCategoriesAndSubcategories(
        shopId
      );
    const data = (response?.data?.result?.data ?? {}) as AllowedCategoriesMap;
    writeCache(shopId, data);
    return data;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

/**
 * Removes every cached allowed-categories entry. Call on logout so no
 * role-restricted data lingers in storage for the next user.
 */
export const clearStockManagerAllowedCategoriesCache = (): void => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => deleteSession(key));
  } catch (error) {
    Sentry.captureException(error, {
      level: "warning",
      tags: { feature: "allowed_categories_cache" },
    });
  }
};
